"use client";

import { Plus } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function ManufacturingPlantLabelingCommonQuestions() {
  const header = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const list = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const footer = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const inquiries = [{
    id: "item-1",
    question: "What materials are best suited for industrial plant labeling?",
    answer: "We specialize in high-durability materials including industrial-grade phenolic, anodized aluminum, and stainless steel. These materials are selected for their resistance to harsh chemical environments, extreme temperatures, and UV exposure, ensuring your facility remains compliant and safe for years.",
    pull: "Engineered for the harshest industrial environments."
  }, {
    id: "item-2",
    question: "How do you ensure compliance with UL 508A standards?",
    answer: "Our labeling solutions are designed to meet the rigorous requirements of UL 508A panel shops. We provide precise, permanent identification that adheres to all safety and regulatory standards, ensuring your control panels pass inspection without delays or rework."
  }, {
    id: "item-3",
    question: "Can you handle large-scale facility labeling projects?",
    answer: "Yes, our production facility is equipped to manage high-volume orders for large-scale manufacturing plants. From initial design and submittal packages to final installation-ready labeling, we provide a streamlined workflow that scales with your project requirements."
  }, {
    id: "item-4",
    question: "Do you provide custom engraving for specific equipment?",
    answer: "Absolutely. We offer precision laser engraving for custom tags, nameplates, and control panel labels. Whether you need specific serial numbers, complex schematics, or custom branding, our team ensures every detail is captured with absolute accuracy.",
    pull: "Precision is the foundation of every label we produce."
  }, {
    id: "item-5",
    question: "What is the typical turnaround time for custom orders?",
    answer: "We prioritize efficiency without compromising quality. Standard orders are typically processed and shipped within 5-7 business days. For complex submittal-heavy projects, we work closely with your team to establish a timeline that aligns with your construction or maintenance schedule."
  }, {
    id: "item-6",
    question: "How do you support specifying engineers?",
    answer: "We act as an extension of your engineering team, providing comprehensive submittal packages, material data sheets, and technical support. We ensure that every label specified in your project documentation is manufactured to exact standards, simplifying the procurement process."
  }];
  return <section data-section-id="4021" id="manufacturing-plant-labeling-common-questions" className="bg-background text-foreground py-24 md:py-32 lg:py-40">
      <div className="mx-auto max-w-7xl px-6 lg:px-12">
        {}
        <div ref={header.ref} className={`grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-16 transition-all duration-1000 ease-out ${header.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="lg:col-span-5">
            <p className="font-mono text-xs uppercase tracking-widest text-primary">
              IV — Inquiries
            </p>
            <h2 className="font-default mt-6 text-5xl font-light leading-[1.05] tracking-tight text-foreground md:text-6xl lg:text-6xl">
              Common
              <br />
              <span className="italic">Questions.</span>
            </h2>
          </div>

          <div className="lg:col-span-6 lg:col-start-7 lg:pt-10">
            <p className="font-default text-lg italic leading-relaxed text-muted-foreground md:text-xl">
              The following answers address the technical and operational concerns
              most frequently raised by our industrial partners — covering
              material durability, regulatory compliance, project scalability,
              and the precision required for modern manufacturing environments.
            </p>
          </div>
        </div>

        {}
        <div ref={list.ref} className={`mx-auto mt-20 max-w-4xl transition-all duration-1000 delay-150 ease-out ${list.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          <Accordion type="single" collapsible className="flex flex-col gap-3">
            {inquiries.map((item, i) => <AccordionItem key={item.id} value={item.id} data-index={i} className="group rounded-lg border-0 bg-card transition-all duration-500 ease-out hover:-translate-y-0.5 hover:bg-card/70 data-[state=open]:bg-card/60">
                <AccordionTrigger className="[&>svg:last-child]:hidden flex w-full items-start justify-between gap-6 rounded-lg px-8 py-8 text-left hover:no-underline md:px-10 md:py-9">
                  <span className="font-default text-lg font-medium leading-snug text-foreground md:text-xl">
                    {item.question}
                  </span>
                  <span aria-hidden="true" className="relative mt-1 flex h-6 w-6 shrink-0 items-center justify-center">
                    <Plus className="h-5 w-5 text-primary transition-all duration-500 ease-out group-data-[state=open]:rotate-45 group-data-[state=open]:scale-110" strokeWidth={1.25} />
                  </span>
                </AccordionTrigger>
                <AccordionContent className="px-8 pb-9 md:px-10 md:pb-10">
                  <div className="border-t border-border pt-6">
                    <p className="font-default text-base leading-[1.85] text-muted-foreground md:text-lg">
                      {item.answer}
                    </p>
                    {item.pull && <p className="font-default mt-6 border-l-2 border-primary/70 pl-5 text-base italic leading-relaxed text-foreground/80 md:text-lg">
                        {item.pull}
                      </p>}
                  </div>
                </AccordionContent>
              </AccordionItem>)}
          </Accordion>
        </div>

        {}
        <div ref={footer.ref} className={`mx-auto mt-20 max-w-4xl text-center transition-all duration-1000 delay-200 ease-out ${footer.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          
        </div>
      </div>
    </section>;
}